/**
 * subRequests/{id} — "I can't teach Thursday, who's free?"
 *
 * Lightweight sub-request board. A staff member files against a specific
 * class + date; other staff see open requests and tap Claim. No approval
 * flow — claiming is first-come-first-served: a transaction guarantees the
 * FIRST claimer wins and anyone tapping after gets a friendly "too late"
 * (see useClaimSubRequest / SubAlreadyClaimedError). The requester sees cover
 * land on the live board the moment it happens; no notification is sent for it
 * (Bobbie, 2026-09-04: "No notifications should happen when something is
 * covered just new requests").
 *
 * Schema:
 *   offeringId    which class needs cover
 *   date          YYYY-MM-DD when
 *   timeHint      optional human time ("5:00 PM") since the offering
 *                 doesn't carry a fixed schedule yet
 *   requesterUid  who's asking
 *   tier          the requester's role at the moment they filed, PINNED
 *                 in the Firestore rules from their users/{uid} doc (see
 *                 TIER SCOPING below). Absent on requests filed before
 *                 2026-08-06.
 *   status        'open' | 'claimed' | 'cancelled'
 *   claimedBy     uid of the cover when claimed; null otherwise
 *   claimedAt     Timestamp when claimed; null otherwise
 *   notes         freeform from requester
 *   audit fields
 *
 * TIER SCOPING (Bobbie, 2026-08-06): "They need a sub board to get subs
 * for themselves. So it should be only offered to other jr assistants...
 * Kim should see all of them. But junior assistance only sees assistance
 * and executive teachers only the executive teacher." Like covers like.
 * Jr Staff are children; their board must never carry adult requests.
 *
 * THE TRAP THIS FILE EXISTS TO AVOID. Because the rules gate reads on the
 * stored `tier`, EVERY list query here must carry a matching tier filter
 * (`==` one tier, or `in` the Sr Staff tiers for an Executive Teacher since
 * 2026-09-28) for a viewer whose rule branch needs one.
 * Firestore does not filter a query it cannot prove, it REFUSES it, and a
 * refused sub-board listener renders as an empty board - which reads as
 * "nobody needs cover" and is strictly worse than the over-sharing it
 * replaced. subTierFilterFor() below is the one place that decision lives,
 * and every query in this file and in payroll.ts goes through it.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { functionHeaders } from './functionSecurity';
import {
  SR_STAFF_SUB_TIERS,
  buildSubBoardQuery,
  buildSubRangeQuery,
  subBoardFromDate,
  subLookBackFromDate,
} from './subQuery';
import { liveBoardSubRequests, visibleSubRequests } from './subRequestVisibility';
import { useAuth } from './auth-context';
import { ROLE_LABELS, STAFF_ROLES, useUserDoc, type UserRole } from './users';
import {
  captureNotificationsForSubRequest,
  markCapturedNotificationsRead,
  notifyUsers,
  type NotifyOptions,
  type NotifyPayload,
} from './notifications';
import type { User } from 'firebase/auth';
import type { MessageAttachment } from './threads';
import { saveAttachmentMessage } from './messageAttachmentWrite';
import { confirmedSubCoversForDate } from './adminSubBoardRows';
import { viewerSaidNotFree, type SubNotFreeMap } from './subNotFree';

export { confirmedSubCoversForDate } from './adminSubBoardRows';

export type SubStatus = 'open' | 'claimed' | 'cancelled';

/**
 * Roles that read every tier of the board rather than just their own: the
 * Director because Bobbie said "Kim should see all of them", the Admin desk
 * because its confirmed-subs view (AdminConfirmedSubBoard) has always covered
 * the whole studio and Bobbie has not ruled on changing that, and Manager as
 * of the 2026-08-13 union ruling. Kept in lockstep with subTierReachable() in
 * shared/firestore.rules - a role listed here MUST be listed there or its
 * query gets denied and the board renders empty, which reads as "nobody needs
 * cover".
 */
const ALL_TIER_ROLES: readonly UserRole[] = ['director', 'foh', 'manager'];

/**
 * THE SR STAFF BOARD: the tiers that share one board (Bobbie, 2026-09-28).
 * Defined in ./subQuery beside the query that carries it, so the rules suite
 * proves the exact list the app ships; the why is written there.
 */
export { SR_STAFF_SUB_TIERS };

/**
 * Roles that SEE every tier but are never PINGED about a request, whatever
 * tier it was filed at, including their own.
 *
 * Bobbie, 2026-09-04: "Can you turn off notifications for sub requests for
 * admin? They need access to the board but not notifications."
 *
 * Seeing and being interrupted are different powers, and this fan-out was the
 * one place in the sub flow that treated them as the same one. The Admin desk
 * reads every tier so it can run the confirmed-subs view, and NONE of that
 * changes: 'foh' stays in ALL_TIER_ROLES above, subTierFilterFor() still
 * returns null for it, seesAllSubTiers() is still true, and subTierReachable()
 * in shared/firestore.rules is untouched. No rules change, so no content bytes
 * spent against the release ceiling.
 *
 * This one list is the whole switch, because nothing else in the flow pings
 * the desk: subReminder.mjs notifies only whoever claimed the cover, and a
 * claim notifies only the requester.
 */
const SUB_NOTIFY_OPT_OUT_ROLES: readonly UserRole[] = ['foh'];

/**
 * Does a staff member in `role` get told about a request filed at `tier`?
 *
 * PURE and exported, so the decision has one home a test can ask directly
 * rather than reaching through a Firestore query to infer it.
 */
export function notifiedAboutSubTier(
  role: UserRole | null | undefined,
  tier: UserRole
): boolean {
  if (!role) return false;
  // Opted out entirely, checked BEFORE the same-tier clause: an Admin filing
  // her own request must not ping the rest of the desk either, which is what
  // "not notifications" means. The Director and any Manager still hear about
  // it, so it is never filed unseen.
  if (SUB_NOTIFY_OPT_OUT_ROLES.includes(role)) return false;
  // Bobbie, 2026-09-04: "Directors should have access to the [jr] staff sub
  // board but no notifications. Jr staff should get notifications for jr staff
  // request." A Jr Staff request reaches the Jr Staff tier and stops there.
  // The Director still READS it, unchanged - she goes and looks, the same
  // arrangement the Admin desk now has.
  if (role === 'director' && tier === 'ensemble') return false;
  // Everyone else is told about exactly the tiers they can see and claim,
  // which since 2026-09-28 means an Executive Teacher hears about a Manager's
  // or a Director's request the same as a fellow Executive Teacher's.
  return subTierReachableBy(role, tier);
}

/**
 * Can this role actually pick up a cover request?
 *
 * THE ADMIN DESK CANNOT, and the home tile spent months saying it could.
 * Tara, an Admin, 2026-09-04, relayed by Bobbie: "I just got a ton of messages
 * about sub needed alerts but when I open sub board I can't see any of the
 * messages? Is that because I am admin and those are jr staff subs requests?"
 *
 * Her guess was wrong and the truth was worse. Her home tile read "6 Sub
 * requests you could claim", counted straight off every OPEN request she can
 * read - and she reads every tier - with nothing asking whether she could
 * claim one. She cannot: 'foh' routes to AdminConfirmedSubBoard, which shows
 * CONFIRMED cover only and carries no Claim action at all. So the tile
 * promised six actions, and the screen it opened could not show a single one
 * of them, because none had been claimed yet. Two surfaces, two different
 * questions, and the number was answering neither.
 *
 * The same three-layer disagreement as the calendar bug: one screen describes
 * a rule, another draws a different set, and nothing keeps them honest.
 */
export function canClaimSubRequests(role: UserRole | null | undefined): boolean {
  if (!role) return false;
  return SUB_CLAIM_CAPABLE_ROLES.includes(role);
}

/** Every role the claimable board (and its Claim button) is actually built
 *  for. 'foh' is absent because it is routed away from that board entirely,
 *  and 'family' because the sub board is staff-only. */
const SUB_CLAIM_CAPABLE_ROLES: readonly UserRole[] = ['director', 'manager', 'company', 'ensemble'];

/**
 * The words every "you could pick one of these up" tile wears, in ONE place.
 *
 * Two screens now draw this tile: the staff home's attention card
 * (pages/landing/StaffHomeContent.tsx) and the dancer home's Jr Staff lane
 * (pages/landing/dancer/DancerSubBoardTile.tsx), because a Jr Staff on her own
 * dancer sign-in is the same staff member as a Jr Staff on an email account
 * (Bobbie, 2026-09-07). Two hand-typed copies of one sentence is how the two
 * screens start describing different features to the same person.
 */
export const SUB_BOARD_TILE_LABEL = 'Sub requests you could claim';

/**
 * How many open requests this viewer could actually pick up: someone else's,
 * still open, on a tier they can reach, and only when their role has a Claim
 * button at all.
 *
 * PURE, so the tile and any future surface ask the same question once.
 */
export function claimableSubCount(
  requests: readonly { status?: string; requesterUid?: string; tier?: string; notFree?: unknown; date?: string; offeringId?: string }[],
  meUid: string | null | undefined,
  role: UserRole | null | undefined
): number {
  if (!meUid || !canClaimSubRequests(role)) return 0;
  return requests.filter((r) => (
    r.status === 'open'
    && r.requesterUid !== meUid
    // Reachability, so a cross-tier reader is never offered a request its own
    // board would not list. subTierReachableBy() asks subTierFilterFor(), the
    // same answer the query itself carries.
    && subTierReachableBy(role, r.tier)
    // "I'm not free" takes it off her count. The entry names every sign-in
    // she holds, so asking with this one uid answers for both.
    && !viewerSaidNotFree(r, [meUid])
  )).length;
}

export {
  SUB_REOPEN_HOURS_BEFORE,
  studioDateStartMs,
  subRequestReopenAtMs,
  subInboxVersionMs,
} from './subInboxVersion.ts';

/**
 * One tier's open requests, for the staff home's Jr Staff tile. Lives in its
 * own importless file so a node test can run it directly; re-exported here so
 * every sub-board caller keeps one import site.
 */
export { openSubCountForTier } from './subTierOpenCount.ts';

/**
 * Tonight's cover, for the Admin desk.
 *
 * Bobbie, 2026-09-04, on a screenshot of Tara's home screen reading "2 Sub
 * requests you could claim": "Admin need a new tool that says - sub happening
 * tonight. And when you click you can see it. Otherwise it should not show up,
 * they can[not] sub, they don't have anything to do with that other than
 * knowing on the day."
 *
 * So the desk gets the one fact it can act on - who is covering what, today -
 * and NOTHING on a night with no cover. A permanent zero row is the noise this
 * card was trimmed to remove, and a row promising an action the desk does not
 * have is what sent Tara looking for a bug in the first place.
 *
 * CLAIMED ONLY. An unclaimed request is not cover "happening tonight", and the
 * desk cannot pick it up, so counting it would rebuild the same false promise
 * in new words.
 */
export function subsHappeningTonight(
  requests: readonly { status?: string; date?: string; claimedBy?: string | null }[],
  todayKey: string
): number {
  return confirmedSubCoversForDate(requests, todayKey).length;
}

/**
 * THE ONE PLACE WHO-SEES-WHICH-TIER IS DECIDED. The tier filter this
 * viewer's queries must carry:
 *
 *   - null for the roles that read every tier (no tier clause at all),
 *   - SR_STAFF_SUB_TIERS for an Executive Teacher (a `where('tier','in',...)`
 *     over the shared Sr Staff board, Bobbie 2026-09-28),
 *   - the role itself for everyone else (a `where('tier','==',...)`).
 *
 * Returns undefined while the role is still resolving: callers must HOLD the
 * query rather than guess, because guessing wrong costs a permission denial
 * and an empty board.
 */
export function subTierFilterFor(
  role: UserRole | null | undefined
): UserRole | readonly UserRole[] | null | undefined {
  if (!role) return undefined;
  if (ALL_TIER_ROLES.includes(role)) return null;
  if (role === 'company') return SR_STAFF_SUB_TIERS;
  return role;
}

/**
 * Can a viewer in `role` see (and, with a Claim-capable role, cover) a
 * request filed at `tier`? The client mirror of the subRequests read rule in
 * shared/firestore.rules, answered FROM subTierFilterFor() so the count, the
 * ping and the query can never disagree. An untagged legacy request reaches
 * only the every-tier roles, as in the rule. The claim itself goes through
 * subClaim, whose server mirror (lib/subTierReach.mjs) gives the same answer.
 */
export function subTierReachableBy(
  role: UserRole | null | undefined,
  tier: string | null | undefined
): boolean {
  const filter = subTierFilterFor(role);
  if (filter === undefined) return false;
  if (filter === null) return true;
  if (!tier) return false;
  return typeof filter === 'string'
    ? tier === filter
    : (filter as readonly string[]).includes(tier);
}

/**
 * Who a request filed by this viewer actually reaches, shown on the
 * file-a-request form so nobody has to guess at the scoping. A request from
 * any senior tier lands on the shared Sr Staff board, so all three say the
 * same thing; a Jr Staff request still reaches Jr Staff only.
 */
export function subTierAudienceLabel(role: UserRole | null | undefined): string {
  if (!role) return '';
  if ((SR_STAFF_SUB_TIERS as readonly UserRole[]).includes(role)) return SR_STAFF_AUDIENCE_LABEL;
  return ROLE_LABELS[role] ?? role;
}

/** The three roles that share the Sr Staff board, named the way ROLE_LABELS
 *  names them, for the file-a-request form and the board's scope line. */
export const SR_STAFF_AUDIENCE_LABEL = 'Executive Teachers, Managers and Directors';

/**
 * Does this viewer's board carry more than one tier? True for the every-tier
 * roles and for an Executive Teacher on the shared Sr Staff board. Drives the
 * per-card tier chip, so a mixed board says whose request each one is.
 */
export function seesSeveralSubTiers(role: UserRole | null | undefined): boolean {
  const filter = subTierFilterFor(role);
  return filter === null || Array.isArray(filter);
}

/**
 * ROLLOUT SAFETY NET for the shared Sr Staff board. The Executive Teacher's
 * listener sends where('tier','in',SR_STAFF_SUB_TIERS), which only the
 * 2026-09-28 rules can prove. The Firestore rules release runs AFTER the Hub
 * bundle is live (.github/workflows/deploy-firestore-rules.yml waits for the
 * staging app first), and a release can be refused at activation, so for a
 * while the new bundle can meet the OLD rules. Those refuse the whole `in`
 * query, and a board that renders empty on a denial reads as "no subs
 * needed", including her own requests that she could see the day before.
 *
 * So when a SHARED-board listener is denied, the hook listens again with the
 * narrow own-tier filter those old rules always proved: the board shrinks
 * back to what it was yesterday instead of going blank. Returns that narrow
 * filter, or null when there is nothing safer to fall back to (a single-tier
 * or every-tier filter, any error other than a denial, or a role that is not
 * on the shared list). The narrow board holds until the listener mounts
 * again, so a reload picks the shared board up once the new rules are live.
 */
export function subTierFallbackOnDenial(
  filter: UserRole | readonly UserRole[] | null | undefined,
  role: UserRole | null | undefined,
  errorCode: string | null | undefined
): UserRole | null {
  if (errorCode !== 'permission-denied') return null;
  if (!Array.isArray(filter) || !role) return null;
  return (filter as readonly UserRole[]).includes(role) ? role : null;
}

/**
 * Does this viewer see every tier? Drives the board's own copy; the rules
 * are the actual boundary.
 */
export function seesAllSubTiers(role: UserRole | null | undefined): boolean {
  return !!role && ALL_TIER_ROLES.includes(role);
}

export const STATUS_LABELS: Record<SubStatus, string> = {
  open: 'Open',
  claimed: 'Claimed',
  cancelled: 'Cancelled',
};

/**
 * Plain-language sentence for a claimed sub request, for the sub board and
 * the calendar pill: "Miss Annie is covering Blue Class on Thu, Jul 9."
 */
export function claimedSubSentence(claimerLabel: string, className: string, dateLabel: string): string {
  return `${claimerLabel} is covering ${className} on ${dateLabel}.`;
}

export type { SubNotFreeMap } from './subNotFree';
export { notFreeSentence, viewerSaidNotFree } from './subNotFree';

export interface SubRequestDoc {
  id: string;
  offeringId: string;
  date: string;
  timeHint: string;
  requesterUid: string;
  /**
   * The requester's role when they filed. Optional because every request
   * filed before 2026-08-06 predates the field; those are visible to the
   * Director and the Admin desk only, until the Director stamps them from
   * the sub board. Never write this from the client by hand - the rules
   * pin it, and a mismatch is rejected.
   */
  tier?: UserRole;
  status: SubStatus;
  claimedBy: string | null;
  claimedAt: Timestamp | null;
  /**
   * THE SUB WINDOW, written by the claim lane and taken off again by
   * netlify/functions/subClassGrantSweep.mjs. Present only while whoever
   * claimed this request still has the class open to them. It is a field on
   * the request rather than a document of its own so the board can say "you
   * have this class until ..." off the listener it already runs.
   */
  classAccess?: SubClassAccess;
  /** The same expiry at the top level, because the sweep filters on a range
   *  and a range filter on a nested field would need an index nobody made. */
  classAccessExpiresAt?: Timestamp;
  /** Counts up once per grant and is never removed, so a revoke can prove it
   *  is closing the grant it read rather than a newer one. */
  classAccessGeneration?: number;
  notes: string;
  attachments?: MessageAttachment[];
  /**
   * Present only on a cover a Director wrote down by hand through
   * netlify/functions/subRecordCover.mjs (packet JS1). The row is born
   * claimed: `claimedBy` is whoever stepped in, `requesterUid` is ALWAYS the
   * recording Director's uid, never the no-show's - the no-show lives only
   * in `recordedCover.noShowUid`. A no-show is an absence, never a sub
   * request (DECISIONS.md entry 40), so every counter reads this marker and
   * counts NOTHING for the requester side.
   */
  recordedCover?: SubRecordedCover;
  /**
   * "I'm not free" answers, one per PERSON, written only by the server lane
   * (subClaim.mjs action 'decline', lib/subDecline.mjs). Each entry's `uids`
   * names every sign-in that person holds. Read through lib/subNotFree.ts.
   */
  notFree?: SubNotFreeMap;
  /** The "date#releasedAtMillis" the 24 hour "NO ONE HAS PICKED UP THIS SUB"
   *  flag went to the Directors for (subReminder.mjs). Server written only. */
  uncoveredFlaggedFor?: string;
  /** When the cover was last handed back, written by the claim lane. */
  releasedAt?: Timestamp;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
}

export interface SubRecordedCover {
  recordedBy: string;
  recordedByName?: string;
  /** A server timestamp is unresolved (FieldValue) until the write round-trips. */
  recordedAt: Timestamp | FieldValue | null;
  /** Canonical uid of the person who did not show, or null when nobody was named. */
  noShowUid: string | null;
}

/**
 * What the claim lane wrote when it opened the class. Read-only here: every
 * field is written by netlify/functions/lib/subClassGrant.mjs with the Admin
 * SDK, and nothing in the app writes one from the client.
 */
export interface SubClassAccess {
  /**
   * THE SEAT. For a linked Jr Staff this is her canonical TEACHING uid, which
   * is not always the uid she signed in with (Bobbie, 2026-09-07: a Jr Staff
   * is one person however she signs in). Read claimedBy on the request, not
   * this, to answer "is this my claim".
   */
  uid: string;
  offeringId: string;
  generation: number;
  grantedAt: Timestamp | null;
  expiresAt: Timestamp | null;
  classEndsAt?: Timestamp | null;
  endSource?: string;
  /** True when the claim is what put this person on the teaching team, which
   *  is the only case in which handing it back takes them off again. */
  addedCoTeacher: boolean;
  addedTeacherName: boolean;
  /** Her other sign-in, present only when the claimer has two. The class is
   *  opened on both and closed on both together. */
  aliasUid?: string;
  addedAliasCoTeacher?: boolean;
  addedAliasTeacherName?: boolean;
  /** The uid that actually pressed the button, when it differs from uid. */
  claimedByUid?: string;
  /**
   * Which of this grant's uids held a TEMPORARY seat on the class, from the
   * offering's own ledger at claim time. Somebody already teaching the class
   * is never in it and never lost anything when the window closed, so this is
   * what decides whether a screen may say the class has closed again.
   */
  temporaryUids?: string[];
}

export interface UseSubRequestsResult {
  loading: boolean;
  requests: SubRequestDoc[];
  error: string | null;
  /**
   * True while this list came from the local cache and the server has not
   * confirmed it yet. Required, not optional - see useOpenInjuryNotes for
   * the same shape and useOfferings' UseOfferingsResult for the same
   * trackFromCache pairing. Only reliably resolves back to false on a
   * content-identical server confirm when trackFromCache is passed true;
   * existing callers keep the exact subscription they always had.
   */
  fromCache: boolean;
}

/**
 * Active sub requests with `date >= today`. Cancelled records stay saved as
 * history but are removed from this live list as soon as the listener sees
 * the cancellation. Sort: open first, then claimed; within each, earliest
 * date first.
 *
 * `subRequests` reads are staff-only under the Firestore rules, so pass
 * `enabled = false` for a family viewer instead of letting the listener
 * hit a permission denial.
 *
 * TIER FILTER: for a Jr Staff viewer this query carries
 * where('tier','==','ensemble'); an Executive Teacher's carries
 * where('tier','in', SR_STAFF_SUB_TIERS) (the shared Sr Staff board,
 * 2026-09-28), falling back to where('tier','==','company') if the rules
 * refuse it (subTierFallbackOnDenial). The read rule gates on that field and
 * Firestore refuses any query it cannot prove. The filter
 * is derived from the viewer's own user doc, so the listener HOLDS (stays
 * loading) until that doc resolves rather than firing an unfiltered query
 * that would be denied. Director and Admin read every tier and send no
 * filter at all. Needs the (tier ASC, date ASC) composite index in
 * shared/firestore.indexes.json.
 *
 * `trackFromCache` opts into `includeMetadataChanges: true` (see
 * useOfferings' doc comment for why); it defaults to false so callers that
 * do not need cache provenance keep the smaller event volume. Since the
 * home-listener dedupe (2026-08-19), HomeTicker and useStaffTileCounts no
 * longer subscribe here at all on the home cards - they are fed from one
 * lifted trackFromCache=true subscription, so they see a handful of extra
 * metadata-only re-renders with identical data, loading, and error
 * semantics.
 */
export function useUpcomingSubRequests(
  enabled = true,
  trackFromCache = false,
  /**
   * Studio days BEFORE today to include as well. 0, the default, is the live
   * board's own `date >= today`. Calendar and Teach pass 1, because a sub's
   * class stays open to her until 24 hours after it and those two screens
   * show her the class she covered for that whole window (see
   * subLookBackFromDate). The extra reads are the requests dated yesterday,
   * a handful, on a listener those screens already run.
   */
  lookBackDays = 0
): UseSubRequestsResult {
  const [requests, setRequests] = useState<SubRequestDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);
  // The EFFECTIVE role, so a Director using "view as" gets that role's
  // slice of the board rather than the whole thing.
  //
  // CHECKED, because a reviewer read it the other way on 2026-08-06 and the
  // answer is load-bearing: useUserDoc() returns `userDoc: effectiveDoc`,
  // and effectiveDoc is `{...userDoc, role: previewRole}` when a preview is
  // active (hub/src/lib/users.ts:405-421, usePreviewRole imported at :49).
  // Use `realRole` from the same hook if you ever need the true one here.
  //
  // Safe either way, because the RULES resolve the real users doc: only an
  // actual Director can preview, preview never targets 'director', and a
  // Director's own rule branch proves any tier filter she sends. So the
  // worst a preview can do is show her less than she is entitled to.
  const { userDoc, loading: meLoading } = useUserDoc();
  const viewerRole = userDoc?.role;
  const tierFilter = subTierFilterFor(viewerRole);

  useEffect(() => {
    if (!enabled) {
      setRequests([]);
      setLoading(false);
      setError(null);
      setFromCache(true);
      return;
    }
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      setFromCache(true);
      return;
    }
    // Role not settled yet. Stay LOADING and send nothing: an unfiltered
    // query from a teacher is a permission denial, and a board that renders
    // empty on a denial reads as "no subs needed".
    if (meLoading) {
      setRequests([]);
      setLoading(true);
      setError(null);
      setFromCache(true);
      return;
    }
    // Settled, and there is still no role: an unverified email, a user doc
    // that never arrived, a signed-out session. STOP, and say so. The first
    // draft of this held `loading` true here, which turned a broken account
    // doc into a spinner that never resolved - a hang is not a safer
    // failure than an error, it is a quieter one.
    if (tierFilter === undefined) {
      setRequests([]);
      setLoading(false);
      setError('We could not tell which sub board is yours. Try signing out and back in.');
      setFromCache(true);
      return;
    }
    // Back to true before we subscribe, the way useSubsForWeek does. The
    // disabled branch above leaves loading false, and the consumers gate on
    // the SAME user doc that settles tierFilter here (Messages' staff inbox,
    // Attendance's sub coverage), so meLoading is already false when enabled
    // flips on and its branch cannot cover this. Without it the inbox settles
    // with no sub rows and the coverage lookup runs over an empty array.
    setLoading(true);
    setError(null);
    // The STUDIO's today, not the device's. Built inline from the device
    // calendar, this bound is tomorrow for any runtime running ahead of
    // Chicago, and `date >= tomorrow` drops today's requests in Firestore
    // before anything downstream can show them. See subBoardFromDate.
    const today = subBoardFromDate();
    // And for the two screens that show a sub her own covers, one studio day
    // earlier (lookBackDays above). Derived from `today`, never from a device
    // date, so the bound is still the studio's.
    const fromDate = subLookBackFromDate(today, lookBackDays);
    // Built by ./subQuery, which the rules suite imports and runs against
    // the emulator for every role. The query proven there and the query
    // sent here are the same code, not two transcriptions of one idea.
    const firestore = db;
    let unsub: () => void = () => {};
    let stopped = false;
    const listen = (filter: UserRole | readonly UserRole[] | null) => {
      const q = buildSubBoardQuery(firestore, filter, fromDate);
      unsub = onSnapshot(
        q,
        // See useOfferings: includeMetadataChanges: false here is the SDK
        // default, byte-identical to the old options-less call.
        { includeMetadataChanges: trackFromCache },
        (snap) => {
          // LIVE BOARD ONLY: a row a Director recorded by hand is hidden here
          // (liveBoardSubRequests), never on this query. Today is allowed when
          // recording, so a same-day recorded row would otherwise render as
          // "Asked by <Director>" on tonight's board. See subRequestVisibility.ts.
          const docs = liveBoardSubRequests(
            visibleSubRequests(
              snap.docs.map(
                (d) => ({ id: d.id, ...(d.data() as Omit<SubRequestDoc, 'id'>) })
              )
            )
          );
          // Re-sort by status priority then date.
          const statusRank: Record<SubStatus, number> = {
            open: 0,
            claimed: 1,
            cancelled: 2,
          };
          docs.sort((a, b) => {
            const sd = statusRank[a.status] - statusRank[b.status];
            if (sd !== 0) return sd;
            return a.date.localeCompare(b.date);
          });
          setRequests(docs);
          setFromCache(snap.metadata.fromCache);
          setLoading(false);
        },
        (err) => {
          // A shared-board query the live rules cannot prove yet: listen
          // again on the viewer's own tier rather than blank the board. See
          // subTierFallbackOnDenial for why this window exists.
          const narrow = subTierFallbackOnDenial(filter, viewerRole, err.code);
          if (narrow && !stopped) {
            listen(narrow);
            return;
          }
          setError(err.message);
          setFromCache(true);
          setLoading(false);
        }
      );
    };
    listen(tierFilter);
    return () => {
      stopped = true;
      unsub();
    };
  }, [enabled, trackFromCache, meLoading, tierFilter, viewerRole, lookBackDays]);

  return { loading, requests, error, fromCache };
}

/**
 * Local YYYY-MM-DD key for a Date, in the viewer's own timezone so a
 * Mon-Sun week lines up with what the Director sees on the calendar.
 */
function subDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Every sub request whose `date` falls in the Mon-Sun week that starts on
 * `weekStart`. Powers the Director's weekly subs report: pick a week, see
 * how many subs happened and who covered each one (including a Jr Assistant
 * cover). Mirrors `useUpcomingSubRequests`' staff-only query shape, just
 * bounded to one week so a Director can look back at past weeks too.
 *
 * DIRECTOR ONLY, and that is now load-bearing rather than a nicety: this
 * query carries NO tier filter, which only the Director and Admin branches
 * of the read rule can prove. `enabled` must be the caller's own Director
 * check (CommandCenter passes `isDirector`). Point this at a teacher and
 * the listener is denied outright, not narrowed.
 */
export function useWeekSubRequests(weekStart: Date, enabled = true): UseSubRequestsResult {
  const [requests, setRequests] = useState<SubRequestDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);

  // Derive stable string keys so the effect only re-subscribes when the
  // selected week actually changes, not on every Date object identity.
  const startKey = subDateKey(weekStart);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const endKey = subDateKey(weekEnd);

  useEffect(() => {
    if (!enabled) {
      setRequests([]);
      setLoading(false);
      setError(null);
      setFromCache(true);
      return;
    }
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      setFromCache(true);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'subRequests'),
      where('date', '>=', startKey),
      where('date', '<=', endKey),
      orderBy('date', 'asc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = visibleSubRequests(
          snap.docs.map(
            (d) => ({ id: d.id, ...(d.data() as Omit<SubRequestDoc, 'id'>) })
          )
        );
        setRequests(docs);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setFromCache(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled, startKey, endKey]);

  return { loading, requests, error, fromCache };
}

/**
 * EVERY JR STAFF SUB REQUEST IN ONE SEASON, for the Jr Staff master report
 * (PACKET S2). Read-only, and a date RANGE rather than the board's
 * upcoming-only bound: the report is a look back, not a to-do list.
 *
 * `tierFilter` is passed rather than derived so the caller says out loud which
 * board it is asking about. The report asks for tier 'ensemble' and nothing
 * else, which a Director's own rule branch proves happily; see
 * buildSubRangeQuery for why this needs no new index.
 *
 * Cancelled rows are dropped by visibleSubRequests, the same filter every live
 * board uses, so a withdrawn request never counts against the person who
 * withdrew it.
 */
export function useSeasonSubRequests(
  startKey: string,
  endKey: string,
  tierFilter: UserRole | null,
  enabled = true
): UseSubRequestsResult {
  const [requests, setRequests] = useState<SubRequestDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);

  useEffect(() => {
    if (!enabled || !startKey || !endKey) {
      setRequests([]);
      setLoading(false);
      setError(null);
      setFromCache(true);
      return;
    }
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      setFromCache(true);
      return;
    }
    setLoading(true);
    setError(null);
    const q = buildSubRangeQuery(db, tierFilter, startKey, endKey);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRequests(
          visibleSubRequests(
            snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SubRequestDoc, 'id'>) }))
          )
        );
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        // A failed read is null on the report, never an empty season. The
        // error string is what makes that difference visible.
        setError(err.message);
        setFromCache(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled, startKey, endKey, tierFilter]);

  return { loading, requests, error, fromCache };
}

export interface NewSubRequestInput {
  id?: string;
  offeringId: string;
  date: string;
  timeHint: string;
  notes: string;
  attachments?: MessageAttachment[];
}

export function newSubRequestId(): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'subRequests')).id;
}

const MAX_SUB_TIME_HINT_LENGTH = 120;

function normalizeSubTimeHint(value: string): string {
  const timeHint = value.trim();
  if (timeHint.length > MAX_SUB_TIME_HINT_LENGTH) {
    throw new Error(`Keep the time note to ${MAX_SUB_TIME_HINT_LENGTH} characters or fewer.`);
  }
  return timeHint;
}

/**
 * THE ROOT CAUSE of "a fellow teacher never saw the request" (task #76):
 * this used to call the shared `notifyAudience(STAFF_ROLES, ...)` helper,
 * which fetches `where('status', '==', 'active')` across the WHOLE users
 * collection, then filters to STAFF_ROLES in JS after the read. Firestore
 * rules can only prove a bare/unfiltered LIST query against ONE disjunct of
 * `allow read`, using ONLY the query's own filters - and a company/ensemble
 * teacher's applicable disjunct also requires `role in [...]` (see
 * shared/firestore.rules `match /users/{uid}`). That condition is not
 * satisfied by a status-only query (a family account can also be status
 * 'active'), so the whole `getDocs()` was PERMISSION_DENIED for any
 * requester who was not the Director or FOH - which is most teachers.
 * `useCreateSubRequest()` swallowed that in a bare `try { } catch { }`, so
 * the request itself posted fine and nobody was ever told: same disease as
 * the bulletins (#155), Studio Board (#170), and mentions (#137) bugs,
 * just one layer deeper (a notify fan-out, not the board's own listener).
 *
 * Filtering role IN THE QUERY, matching the isActiveStaffByDoc() rule
 * branch exactly, keeps this provable for every role that can file a
 * request (director, company, ensemble, foh all satisfy it).
 */
/**
 * TIER SCOPING lands here too, and it has to. Bobbie: a sub board "should
 * be only offered to other jr assistants". A notification is the OFFER.
 * Telling every Executive Teacher that a Jr assistant needs cover sends
 * them to a board where the request is invisible and unclaimable - a
 * dead-end ping, which is the empty-board failure wearing a different hat.
 *
 * So the fan-out narrows to the people who can actually act: the same
 * tier, plus the Director, plus the Admin desk (which reads every tier).
 * The narrowing happens in JS, deliberately, AFTER the provable query
 * above - tightening the query itself would put a second `in` filter in
 * front of a rule branch that was written for this exact shape, which is
 * the #76 bug this function was created to fix.
 */
async function notifyStaffInTier(
  tier: UserRole,
  payload: NotifyPayload,
  options: NotifyOptions
): Promise<void> {
  if (!db) return;
  const snap = await getDocs(
    query(
      collection(db, 'users'),
      where('status', '==', 'active'),
      where('role', 'in', STAFF_ROLES)
    )
  );
  const uids = snap.docs
    .map((d) => d.data() as { uid?: string; role?: UserRole })
    .filter((u) => notifiedAboutSubTier(u.role, tier))
    .map((u) => u.uid)
    .filter((uid): uid is string => !!uid);
  await notifyUsers(uids, payload, options);
}

export function useCreateSubRequest() {
  const { user } = useAuth();
  const { userDoc } = useUserDoc();
  return async (input: NewSubRequestInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    if (!input.offeringId) throw new Error('Pick a class.');
    if (!input.date) throw new Error('Pick a date.');
    const timeHint = normalizeSubTimeHint(input.timeHint);
    // The rules pin `tier` to this account's real users doc role, so a
    // wrong value here is a rejected write, not a mis-scoped request. We
    // still refuse to send when the role has not resolved, because the
    // error a rule denial produces says nothing useful to a teacher.
    const tier = userDoc?.role;
    if (!tier) throw new Error('Still loading your account. Try that again in a second.');
    const requestId = input.id ?? newSubRequestId();
    if (input.attachments?.length) {
      await saveAttachmentMessage({
        user,
        action: 'subRequest',
        targetId: requestId,
        writeId: crypto.randomUUID(),
        payload: { offeringId: input.offeringId, date: input.date, timeHint, notes: input.notes },
        attachments: input.attachments,
      });
    } else {
      // Use the same preallocated id for the stored request, notifications,
      // return value, and (when present) attachment authorization.  addDoc()
      // used to mint a second id here, leaving text-only notifications linked
      // to a request that did not exist.
      await setDoc(doc(db, 'subRequests', requestId), {
        offeringId: input.offeringId,
        date: input.date,
        timeHint,
        requesterUid: user.uid,
        tier,
        status: 'open' as const,
        claimedBy: null,
        claimedAt: null,
        notes: input.notes.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    // Let the people who can cover it know (best-effort broadcast - the
    // board post above already succeeded either way). Logged, not
    // swallowed silently, so a future regression here shows up somewhere.
    try {
      await notifyStaffInTier(tier, {
        kind: 'sub',
        title: 'A sub is needed',
        body: timeHint ? `${input.date} at ${timeHint}` : input.date,
        // The request itself, so a Manager (who holds both boards) lands on
        // the board it sits on. The server writes the same link.
        link: `/subs#request-${requestId}`,
      }, { subRequest: { id: requestId } });
    } catch (err) {
      console.error('[subs] staff notify failed', err);
    }
    return requestId;
  };
}

/* ---------- Record a cover: the Director's after-the-fact door ---------- */

export interface RecordCoverInput {
  offeringId: string;
  /** YYYY-MM-DD, today or earlier in studio time. */
  date: string;
  /** Whoever stepped in. Either of a Jr Staff's sign-ins is fine; the lane writes her canonical uid. */
  coverUid: string;
  /** Who did not show, or '' when nobody is being named. */
  noShowUid: string;
  timeHint: string;
  note: string;
}

export interface RecordCoverResult {
  requestId: string;
  /** True when this exact cover was already on the books; nothing changed. */
  alreadyRecorded: boolean;
  /** True when the row existed but had been unclaimed, cancelled or reclaimed
   *  since, and this recording put the credit back on the books. */
  repaired: boolean;
  offeringId: string;
  className: string;
  date: string;
  coverName: string;
  noShowName: string;
}

/** Codes the record lane returns, in the words the Director needs. */
const RECORD_COVER_ERROR_COPY: Record<string, string> = {
  sign_in_required: 'Sign in again, then record it.',
  origin_invalid: 'Reload the Hub and try that again.',
  not_allowed: 'Only a Director can record a cover.',
  server_not_configured: 'The studio server is not set up for this yet. Tell Kim.',
};

/**
 * Write down a sub that was arranged off the board, so whoever stepped in
 * gets the credit on /payroll and the Jr Staff report. Bobbie, 2026-09-22:
 * "a director may have last min someone sub for someone else and they need a
 * way to manually add the sub info themselves so jr staff especially gets
 * credit."
 *
 * Goes through netlify/functions/subRecordCover.mjs, never a client write:
 * the create rule pins requesterUid to the caller and refuses a row born
 * claimed, and a rules change costs bytes the file does not have. The lane
 * decides who is a Director from the caller's own users doc, refuses a class
 * still ahead (that is the board's job, and the class opens to a claimer),
 * and never opens the class or notifies anyone.
 */
export function useRecordCover() {
  const { user } = useAuth();
  return async (input: RecordCoverInput): Promise<RecordCoverResult> => {
    if (!user) throw new Error('Sign in required.');
    if (!input.offeringId) throw new Error('Pick the class.');
    if (!input.date) throw new Error('Pick the date.');
    if (!input.coverUid) throw new Error('Pick who stepped in.');
    const response = await fetch('/.netlify/functions/subRecordCover', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify({
        offeringId: input.offeringId,
        date: input.date,
        coverUid: input.coverUid,
        noShowUid: input.noShowUid || '',
        timeHint: input.timeHint.trim(),
        note: input.note.trim(),
      }),
    });
    const result = await response.json().catch(() => ({})) as Partial<RecordCoverResult> & {
      error?: string;
      message?: string;
    };
    if (!response.ok || result.error) {
      throw new Error(
        result.message
        || RECORD_COVER_ERROR_COPY[result.error ?? '']
        || 'Could not record that. Try again.'
      );
    }
    return {
      requestId: result.requestId ?? '',
      alreadyRecorded: result.alreadyRecorded === true,
      repaired: result.repaired === true,
      offeringId: result.offeringId ?? input.offeringId,
      className: result.className ?? '',
      date: result.date ?? input.date,
      coverName: result.coverName ?? '',
      noShowName: result.noShowName ?? '',
    };
  };
}

/** Thrown when a claim loses the race — someone else grabbed it first. */
export class SubAlreadyClaimedError extends Error {
  constructor() {
    super('Too late — someone else just grabbed this one.');
    this.name = 'SubAlreadyClaimedError';
  }
}

/**
 * What the claim lane hands back: the class it just opened, and the moment it
 * closes again.
 */
export interface SubClaimResult {
  offeringId: string;
  className: string;
  date: string;
  /**
   * DID A WINDOW ACTUALLY OPEN. False when the class had already come and
   * gone by more than a day, which is a real thing to claim (somebody covered
   * it, the board should say so) and opens nothing at all. Every screen that
   * says "you have this class until ..." has to read this first: the sentence
   * used to be printed off expiresAtMs alone, over a date already past.
   */
  opened: boolean;
  /** Epoch milliseconds, and ZERO whenever `opened` is false, so a caller that
   *  only reads the number cannot promise a window either. */
  expiresAtMs: number;
  /** False when this person was already on the class teaching team, in which
   *  case handing the request back leaves them on it. */
  addedCoTeacher: boolean;
}

/** Codes the claim lane returns, in the words the person tapping Claim needs. */
const CLAIM_ERROR_COPY: Record<string, string> = {
  sign_in_required: 'Sign in again, then pick this one up.',
  origin_invalid: 'Reload the Hub and try that again.',
  not_allowed: 'That request is not on your board.',
  sub_request_missing: 'This request is no longer on the board.',
  sub_request_changed: 'This request changed while you were reading it. Open the board again.',
  not_claimed: 'Nobody is covering this one right now.',
  recorded_cover: 'This cover was written down by a Director. She can remove it from the Subbing report.',
  server_not_configured: 'The studio server is not set up for sub claims yet. Tell Kim.',
};

/**
 * THE CLAIM, AND THE CLASS THAT COMES WITH IT.
 *
 * Bobbie, 2026-09-07: "if a junior staff says that they will sub for a class
 * that they have access to that class from when they pick it up... It should
 * be available to them until after the classes happened by 24 hours." Same
 * for an Executive Teacher, and for anyone else who can cover.
 *
 * SO THE CLAIM NO LONGER HAPPENS HERE. It happens in
 * netlify/functions/subClaim.mjs, which claims the request and writes the
 * temporary teaching-team seat in ONE Admin SDK transaction. A client cannot
 * write another person onto a class teaching team, and should not be able to,
 * so the two halves could never have been atomic on this side of the wire.
 *
 * The rule that used to allow this write, claimSubRequestShape() in
 * shared/firestore.rules, is deliberately UNTOUCHED: closing it is a rules
 * change and rules changes cost content bytes against a ceiling with about 75
 * of them left. Nothing in the app uses it any more, so a later rules PR can
 * shut it with no other work.
 *
 * IF THE SEAT CANNOT BE WRITTEN, THE CLAIM DOES NOT HAPPEN. A class whose
 * teaching team is full, or which has no lead teacher yet, refuses the claim
 * with a sentence naming the class, so Kim can go and free a seat. Bobbie's
 * rule is that access comes WITH the claim; a claim that quietly succeeded
 * without it would be the same half-answer the hand-worked interim already
 * was.
 */
export function useClaimSubRequest() {
  const { user } = useAuth();
  return async (id: string): Promise<SubClaimResult> => {
    if (!user) throw new Error('Sign in required.');
    const response = await fetch('/.netlify/functions/subClaim', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify({ action: 'claim', requestId: id }),
    });
    const result = await response.json().catch(() => ({})) as {
      error?: string;
      message?: string;
      offeringId?: string;
      className?: string;
      date?: string;
      opened?: boolean;
      expiresAtMs?: number;
      addedCoTeacher?: boolean;
    };
    if (!response.ok || result.error) {
      // FIRST COME, FIRST SERVED, unchanged: the lane runs the same
      // open-only transaction the client used to, so anyone tapping second
      // still gets the friendly "too late" rather than stomping the cover.
      if (result.error === 'already_claimed') throw new SubAlreadyClaimedError();
      throw new Error(
        result.message
        || CLAIM_ERROR_COPY[result.error ?? '']
        || 'Could not pick this one up. Try that again.'
      );
    }

    // Claiming IS acting on the source: clear the claimer's own "Sub
    // needed" cards for this request, mirroring the board fix's
    // capture-then-clear pattern (notificationMatchesDiscussion, commit
    // fb9dff2c). Best-effort and never surfaced as a claim error - the
    // claim itself already committed above.
    try {
      const captured = await captureNotificationsForSubRequest(id);
      await markCapturedNotificationsRead(captured);
    } catch (e) {
      console.error('useClaimSubRequest: clearing sub-request notifications failed', e);
    }

    /* NO PING WHEN A REQUEST IS COVERED, only when one is filed. Bobbie,
       2026-09-04: "No notifications should happen when something is covered
       just new requests." The requester's "Your sub request was claimed" row
       used to be sent here and is gone, server side too. She still learns it
       is covered: the board's own line flips to name who took it, and the
       capture-then-clear above has already removed her stale "Sub needed"
       card, so nothing is left claiming the request is still open. */
    /* FAMILIES ARE TOLD THE DAY BEFORE, not the instant cover is arranged.
       Bobbie, 2026-09-04: "24 [hours] before the class with a sub they can get
       a notification tell them [which] class will have a sub [and] the name of
       the teacher. That's it no other notifications about subs." Cover
       arranged three weeks out used to tell every family three weeks early,
       which is news they cannot use and will have forgotten by the day. The
       heads-up now comes from sendFamilyHeadsUp in
       netlify/functions/subReminder.mjs, which also names the sub. */
    const expiresAtMs = Number(result.expiresAtMs) || 0;
    return {
      offeringId: result.offeringId ?? '',
      className: result.className ?? '',
      date: result.date ?? '',
      // BOTH HALVES HAVE TO AGREE before a screen promises anything. An older
      // lane that answers no `opened` at all is read as opened only when it
      // also handed back a real moment to close at.
      opened: result.opened !== false && expiresAtMs > 0,
      expiresAtMs: result.opened === false ? 0 : expiresAtMs,
      addedCoTeacher: result.addedCoTeacher === true,
    };
  };
}

/**
 * "until Saturday, November 1 at 8:00 PM" - studio time, always, and it says
 * so, because the container clock and half the phones in this studio are not
 * on Central and a window people plan around cannot be ambiguous about which
 * clock it means.
 */
export function subAccessUntilLabel(expiresAtMs: number): string {
  if (!expiresAtMs) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(expiresAtMs));
}

/**
 * The copy the board card and the claim confirmation BOTH show. One
 * definition, because "one fact in two places will drift" is this repo's own
 * standing lesson and two screens promising different windows would be the
 * worst version of it.
 */
export function subAccessSentence(expiresAtMs: number): string {
  const label = subAccessUntilLabel(expiresAtMs);
  if (!label) return '';
  return `You have this class until ${label} Central. Everything a teacher of it sees is open to you until then.`;
}

/**
 * The other half of the same fact: the window this claim opened has closed.
 *
 * SAID WITH THE REAL MOMENT, never "24 hours on". The window is the class end
 * plus 24 hours, and when the Hub has no time on file for that class the
 * fallback is the end of the studio DAY plus 24, which lands up to 48 hours
 * after the class. A hardcoded "24 hours on" was simply wrong there.
 *
 * ONLY EVER SHOWN TO SOMEBODY WHOSE ACCESS ACTUALLY WENT. A teacher or
 * co-teacher who covered her own class keeps her real seat, and the guide
 * promises her in as many words that nothing changes for her; telling her the
 * class had closed would contradict the guide and the screen she is looking
 * at. The caller gates on the grant's own addedCoTeacher flags.
 */
export function subAccessClosedSentence(expiresAtMs: number): string {
  const label = subAccessUntilLabel(expiresAtMs);
  if (!label) return '';
  return `This class closed to you again at ${label} Central. You are still down as having covered it.`;
}

/**
 * Stamp one legacy request with the tier its requester actually holds.
 *
 * Requests filed before 2026-08-06 have no `tier`, so under the scoped
 * read rule they are visible to the Director and the Admin desk only. This
 * puts them back on the right board.
 *
 * DIRECTOR-TAPPED, ONE AT A TIME, ON PURPOSE. It is exported as a plain
 * function with no auto-run anywhere, because "fix it up if it looks
 * missing" on a page load is exactly the bug in docs/lessons.md's
 * "A READ SHOULD NEVER WRITE": the demo data that kept reappearing did so
 * because opening a page silently re-seeded it. A backfill that runs when
 * Kim looks at the board would rewrite studio data she never asked to
 * change, and she would have no way to tell it had happened.
 *
 * The rules re-derive the role from the REQUESTER's user doc and reject a
 * value that does not match, reject a doc that already has a tier, and
 * reject any caller who is not the Director. The role passed here is only
 * what the client believes; the rule is what decides.
 */
export async function backfillSubRequestTier(id: string, requesterRole: UserRole): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'subRequests', id), {
    tier: requesterRole,
    updatedAt: serverTimestamp(),
  });
}

/**
 * HAND IT BACK, AND CLOSE THE CLASS WITH IT.
 *
 * Same lane as the claim, for the same reason: the temporary teaching-team
 * seat has to come off in the same transaction that reopens the request, and
 * only the Admin SDK can write it. The lane mirrors both rule branches, so
 * the person covering hands their own back and a Director can hand back
 * anybody's (which is what reassigning a sub actually is).
 *
 * IT ONLY TAKES BACK WHAT THE CLAIM GAVE. A sub who was already a co-teacher
 * of that class stays one; the grant records which it was.
 */
export function useUnclaimSubRequest() {
  const { user } = useAuth();
  return async (id: string): Promise<void> => {
    if (!user) throw new Error('Sign in required.');
    const response = await fetch('/.netlify/functions/subClaim', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify({ action: 'unclaim', requestId: id }),
    });
    const result = await response.json().catch(() => ({})) as { error?: string; message?: string };
    if (!response.ok || result.error) {
      throw new Error(
        result.message
        || CLAIM_ERROR_COPY[result.error ?? '']
        || 'Could not hand this one back. Try that again.'
      );
    }
  };
}

/**
 * "I'M NOT FREE" (Bobbie, 2026-08-06: "This needs a i'm not free"). Same lane
 * and same refusals as a claim, and it opens and closes nothing: it writes one
 * answer per person onto the request so the requester and a Director can see
 * who already said no. lib/subDecline.mjs.
 */
export function useDeclineSubRequest() {
  const { user } = useAuth();
  return async (id: string): Promise<void> => {
    if (!user) throw new Error('Sign in required.');
    const response = await fetch('/.netlify/functions/subClaim', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify({ action: 'decline', requestId: id }),
    });
    const result = await response.json().catch(() => ({})) as { error?: string; message?: string };
    if (!response.ok || result.error) {
      throw new Error(
        result.message
        || CLAIM_ERROR_COPY[result.error ?? '']
        || 'Could not save that. Try again.'
      );
    }
  };
}

/** Take back your own "I'm not free". Same lane, action 'undecline'. */
export function useUndeclineSubRequest() {
  const { user } = useAuth();
  return async (id: string): Promise<void> => {
    if (!user) throw new Error('Sign in required.');
    const response = await fetch('/.netlify/functions/subClaim', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify({ action: 'undecline', requestId: id }),
    });
    const result = await response.json().catch(() => ({})) as { error?: string; message?: string };
    if (!response.ok || result.error) {
      throw new Error(
        result.message
        || CLAIM_ERROR_COPY[result.error ?? '']
        || 'Could not save that. Try again.'
      );
    }
  };
}

export async function cancelSubRequest(id: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'subRequests', id), {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Task #79: fix a typo'd date/time or add a note on an OPEN request in
 * place, instead of cancelling it and posting a new one. Cancel-and-repost
 * used to be the only path, which threw away the request's own identity
 * (anything that linked to it, like a notification, pointed at a dead doc)
 * and could race a sub who was already mid-claim on the original.
 *
 * Matches editOpenSubRequestShape() in shared/firestore.rules: requester or
 * Director only, request must still be open, status itself never changes
 * through this path.
 */
export async function editSubRequest(id: string, input: NewSubRequestInput): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  if (!input.offeringId) throw new Error('Pick a class.');
  if (!input.date) throw new Error('Pick a date.');
  const timeHint = normalizeSubTimeHint(input.timeHint);
  await updateDoc(doc(db, 'subRequests', id), {
    offeringId: input.offeringId,
    date: input.date,
    timeHint,
    notes: input.notes.trim(),
    updatedAt: serverTimestamp(),
  });
}

export async function editSubRequestWithAttachments(
  user: User,
  id: string,
  input: NewSubRequestInput,
  attachments: MessageAttachment[],
): Promise<void> {
  const timeHint = normalizeSubTimeHint(input.timeHint);
  await saveAttachmentMessage({
    user,
    action: 'subRequest',
    targetId: id,
    writeId: crypto.randomUUID(),
    payload: { offeringId: input.offeringId, date: input.date, timeHint, notes: input.notes },
    attachments,
  });
}
